import { Link } from 'react-router-dom';
import { WordOfTheDayCard } from './WordOfTheDayCard';
import { StoryBarSlot } from './StoryBarSlot';
import { useTranslation } from 'react-i18next';

import { useChatUnread, useCuratorChat } from '@/hooks/useCuratorChat';
import { useWeekEvents } from '@/hooks/useWeekEvents';
import { deadlineNear, isPast, sameDay } from '@/lib/calendarItems';
import { Avatar } from '@/components/ui/Avatar';
import { ContinueLearningCard } from '@/components/learning/ContinueLearningCard';
import { cn } from '@/lib/utils';

/**
 * Главный экран телефона — по макету, утверждённому заказчиком 23.08.2026.
 *
 * ПОЧЕМУ ОТДЕЛЬНЫЙ КОМПОНЕНТ, а не набор `md:`-классов на общей странице.
 * Здесь другая КОМПОЗИЦИЯ, а не та же в узкой колонке: расписание переехало
 * сюда недельной строкой, витрина упражнений стала горизонтальной каруселью,
 * задачи собраны в один тонированный блок вместо плашки на строку. Пытаться
 * выразить это классами видимости — значит держать в одном файле две разметки
 * и ломать обе одной правкой.
 *
 * Данные компонент НЕ запрашивает сам: их уже загрузила страница, и повторные
 * запросы означали бы вторую волну сетевых обращений на самом первом экране.
 * Исключение — неделя: её считает общий хук `useWeekEvents`, тот же, что у
 * карточек широкого экрана и страницы расписания.
 */

type Props = {
  name: string;
  avatarUrl?: string;
  streak: number;
  unread: number;
  /** `lessonNumber` — урок, на котором ученик сейчас (`lessonPosition`). */
  course?: { id: string; title: string; lessonNumber: number; lessonsTotal: number; percent: number };
  tasks: { id: string; to: string; title: string; meta: string; done: boolean }[];
  onToggleTask: (id: string) => void;
  tiles: { to: string; icon: string; label: string }[];
};

export function MobileHome({
  name,
  avatarUrl,
  streak,
  unread,
  course,
  tasks,
  onToggleTask,
  tiles,
}: Props) {
  const { t, i18n } = useTranslation();
  // Значок чата в шапке — только при праве chat и назначенном кураторе (14.09.2026).
  const { showCuratorEntry } = useCuratorChat();
  // Непрочитанное от куратора: спрашивается при каждом открытии главной и при возврате приложения из фона.
  const chatUnread = useChatUnread();

  /**
   * Неделя, выбранный день и его строки — общий расчёт `useWeekEvents` (02.10.2026). До этого здесь лежала своя
   * копия, и она брала только события платформы: личную запись ученик видел в расписании, а на главной читал
   * «занятий нет».
   *
   * ⚠️ РАНЬШЕ НАЖАТИЕ НА ДЕНЬ ОТКРЫВАЛО ОТДЕЛЬНЫЙ ЭКРАН РАСПИСАНИЯ. Заказчик
   * 24.08.2026: «нажимаешь на календарь — открывается календарь, я не так
   * просил, и в макетах не так». В макете день ВЫБИРАЕТСЯ на месте, а строка
   * под полоской показывает занятие выбранного дня. Переход на расписание
   * остаётся только у этой строки.
   *
   * ⚠️ ПОКАЗЫВАЕМ ВСЕ СОБЫТИЯ ДНЯ, А НЕ ПЕРВОЕ. До 05.09.2026 бралось `dayList[0]`, и в день
   * с двумя занятиями виджет упрямо показывал только одно — заказчик поймал это
   * на дне, где были и его комната, и моя.
   */
  const { today, days, eventsOf, picked, setPicked, shownDay, rows, fallback, personalToday } = useWeekEvents();

  // ⚠️ Не больше трёх: виджет на главной, а не расписание. Остальные — по ссылке
  // «ещё N», чтобы блок не разъезжал полэкрана в насыщенный день. Если в дне пусто
  // и день не выбирали — одно ближайшее вообще, чтобы строка не была прочерком.
  const SHOWN = fallback ? 1 : 3;
  const shownEvents = rows.slice(0, SHOWN);
  const moreCount = fallback ? 0 : rows.length - shownEvents.length;

  const dayNames = days.map((d) =>
    d.toLocaleDateString(i18n.language, { weekday: 'short' }).replace('.', ''),
  );
  const doneCount = tasks.filter((x) => x.done).length;

  return (
    <div className="space-y-3 pb-2">
      {/* ── Шапка: кто я, куда иду, что нового ─────────────────────────────── */}
      <div className="flex items-center gap-2 pb-1 pt-1.5">
        <Link to="/app/profile" aria-label={t('nav.profile')} className="flex-none">
          <Avatar name={name} src={avatarUrl} size="md" />
        </Link>
        <div className="min-w-0 flex-1">
          <div className="truncate font-display text-[17px] font-bold leading-tight">
            {t('dashboard.greeting', { name })}
          </div>
          {/* ⚠️ УРОВНЯ ЗДЕСЬ БОЛЬШЕ НЕТ — РЕШЕНИЕ ЗАКАЗЧИКА 07.09.2026.
              На телефоне стояло «A1 → A2» (уровень ученика), а на компьютере в
              карточке курса — «B2–C2» (уровень самого курса). Вещи разные, но
              человек видит на одном экране два несовпадающих уровня и не знает,
              какому верить. Уровень, которому нельзя верить, хуже отсутствия. */}
          {streak > 0 && (
            <div className="mt-0.5 truncate text-xs text-on-surface-variant">
              {t('dashboard.streakDays', { count: streak })} 🔥
            </div>
          )}
        </div>
        {/* ⚠️ До 14.09.2026 значок рисовался всем без проверки: ученик без чата
            нажимал и молча возвращался на главную. Нет куратора — нет значка. */}
        {showCuratorEntry && (
          <Link
            to="/app/chat"
            aria-label={t('nav.chat')}
            className="relative grid h-[38px] w-[38px] flex-none place-items-center rounded-full bg-surface-container-low text-on-surface"
          >
            <span className="material-symbols-outlined text-[19px]">chat</span>
            {/* Точка — куратор ответил. Пуша об этом в 1.1.1 нет: ученик узнаёт только отсюда (`useChatUnread`). */}
            {chatUnread > 0 && (
              <span data-chat-unread className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-primary" />
            )}
          </Link>
        )}
        <Link
          to="/app/notifications"
          aria-label={t('nav.notifications')}
          className="relative grid h-[38px] w-[38px] flex-none place-items-center rounded-full bg-surface-container-low text-on-surface"
        >
          <span className="material-symbols-outlined text-[19px]">notifications</span>
          {unread > 0 && (
            <span className="absolute -right-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-primary px-1 text-[9px] font-bold text-on-primary">
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </Link>
      </div>

      {/* ── Кружки историй и «Актуальное» (сторис 2.0, макет 1 от 13.10.2026) ──
          До 13.10 на телефоне их не было вовсе: кружки рисовала только раскладка компьютера (техдолг 240).
          Ленивым куском и только у ученика с ключом `stories` — у бесплатника здесь пусто и без запросов. */}
      <StoryBarSlot variant="mobile" />

      {/* ── Единственный крупный оранжевый объект экрана — продолжить курс ──
          Правило макета: один такой блок на экран, остальное приглушено. */}
      {/* Разметка блока — общая с компьютером и страницей курса (11.09.2026). На главной
          «Все уроки» нет: «Продолжить» и так ведёт в список уроков курса. */}
      {course && (
        <ContinueLearningCard
          variant="mobile"
          title={course.title}
          lessonNumber={course.lessonNumber}
          lessonsTotal={course.lessonsTotal}
          percent={course.percent}
          continueTo={`/app/lessons/marathon/${course.id}`}
        />
      )}

      {/* ── Неделя: серия, занятия дня и ближайшее занятие одной строкой ────
          Это и есть бывшая вкладка «Расписание»: пятый пункт меню на узком
          экране съедал подписи, а расписание нужно видеть сразу.
          ⚠️ В ПРИЛОЖЕНИИ ЭТА СТРОКА — ЕДИНСТВЕННЫЙ ВХОД В КАЛЕНДАРЬ (сборка 1.1.1, решение
          владельца 02.10.2026): вкладки календаря нет. С 26.09 по 02.10 строка вырезалась
          при сборке вместе с маршрутом. */}
      <div className="rounded-[26px] bg-surface-container-low px-3.5 pb-2.5 pt-3">
        <div className="grid grid-cols-7 gap-0.5">
          {days.map((d, i) => {
            const isToday = sameDay(d, today);
            const dayEvents = eventsOf(d);
            const past = d < today && !isToday;
            return (
              <button
                key={d.toISOString()}
                type="button"
                onClick={() => setPicked(sameDay(d, shownDay) && picked ? null : d)}
                className="flex flex-col items-center gap-1.5"
                aria-label={d.toLocaleDateString(i18n.language, { day: 'numeric', month: 'long' })}
                aria-pressed={sameDay(d, shownDay)}
              >
                <span className="text-[10px] font-semibold text-on-surface-variant">{dayNames[i]}</span>
                <span
                  className={cn(
                    'grid h-7 w-7 place-items-center rounded-full text-xs font-semibold transition',
                    sameDay(d, shownDay)
                      ? 'bg-primary text-on-primary'
                      : isToday
                        ? 'ring-2 ring-primary/45 text-on-surface'
                        : past
                          ? 'bg-info-container text-info'
                          : 'text-on-surface',
                  )}
                >
                  {d.getDate()}
                </span>
                <span
                  className={cn(
                    'h-[5px] w-[5px] rounded-full',
                    dayEvents.length === 0
                      ? 'bg-transparent'
                      : sameDay(d, shownDay)
                        ? 'bg-primary'
                        : 'bg-outline',
                  )}
                />
              </button>
            );
          })}
        </div>
        <div className="mt-2.5 border-t border-outline pt-2.5">
          {shownEvents.length === 0 ? (
            <Link to="/app/schedule" className="flex items-center gap-3">
              <span className="flex-none text-xs font-semibold text-on-primary-container">—</span>
              <span className="min-w-0 flex-1 truncate text-[12.5px] font-semibold text-on-surface">
                {picked
                  ? t('dashboard.noPlanOnDay', {
                      day: shownDay.toLocaleDateString(i18n.language, { day: 'numeric', month: 'long' }),
                    })
                  : t('dashboard.noPlan')}
              </span>
              <span className="material-symbols-outlined flex-none text-[15px] text-on-surface-variant">
                chevron_right
              </span>
            </Link>
          ) : (
            <ul className="space-y-1.5">
              {shownEvents.map((e) => (
                <li key={e.id}>
                  {/* Начавшееся сегодня остаётся в списке, но приглушённым: точка под днём
                      и строка обязаны совпадать (02.10.2026). */}
                  <Link
                    to="/app/schedule"
                    className={cn('flex items-center gap-3', isPast(e, today) && 'opacity-50')}
                  >
                    <span className="flex-none text-xs font-semibold tabular-nums text-on-primary-container">
                      {e.start.toLocaleTimeString(i18n.language, {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-[12.5px] font-semibold text-on-surface">
                      {e.title}
                    </span>
                    {deadlineNear(e, today) && (
                      <span className="flex-none rounded-full bg-primary-container px-2 py-0.5 text-[10px] font-bold text-on-primary-container">
                        {t('schedule.deadlineNear')}
                      </span>
                    )}
                    <span className="material-symbols-outlined flex-none text-[15px] text-on-surface-variant">
                      chevron_right
                    </span>
                  </Link>
                </li>
              ))}
              {moreCount > 0 && (
                <li>
                  <Link
                    to="/app/schedule"
                    className="block pl-[46px] text-[11.5px] font-semibold text-primary"
                  >
                    {t('dashboard.moreEvents', { count: moreCount })}
                  </Link>
                </li>
              )}
            </ul>
          )}
        </div>
      </div>

      {/* ── Задачи дня: ОДИН тонированный блок, а не плашка на строку ────────
          ⚠️ В пустой день блок прячется целиком (решение владельца 01.09.2026):
          заголовок «Задания на сегодня» над пустотой читается как поломка.
          ⚠️ СЕГОДНЯШНИЕ ЛИЧНЫЕ ЗАПИСИ — ЗДЕСЬ ЖЕ, после заданий куратора (02.10.2026):
          ученик записал себе «в 18:00 эссе» и ждёт увидеть это среди дел дня. Галочки
          у записи нет — отметки «сделано» у личных записей сервер не хранит, а
          отметка в браузере пропала бы на другом устройстве. Счётчик считает только
          задания с галочкой. ── */}
      {(tasks.length > 0 || personalToday.length > 0) && (
      <>
      <div className="flex items-baseline justify-between px-0.5 pt-1">
        <h2 className="font-display text-[17px] font-bold">{t('dashboard.tasksTitle')}</h2>
        {tasks.length > 0 && (
          <span className="text-xs font-semibold text-on-surface-variant">
            {doneCount}/{tasks.length}
          </span>
        )}
      </div>
      <div className="rounded-[26px] bg-surface-container-low px-4">
        {tasks.map((task, i) => (
          <div
            key={task.id}
            className={cn(
              'flex items-center gap-3 py-3',
              (i < tasks.length - 1 || personalToday.length > 0) && 'border-b border-outline',
            )}
          >
            <button
              type="button"
              onClick={() => onToggleTask(task.id)}
              aria-label={task.title}
              aria-pressed={task.done}
              className={cn(
                'grid h-6 w-6 flex-none place-items-center rounded-full border-[2.75px]',
                task.done ? 'border-info bg-info/10' : 'border-info',
              )}
            >
              {task.done && (
                <span className="material-symbols-outlined text-[13px] font-bold text-info">check</span>
              )}
            </button>
            {/* ⚠️ БЕЗ ПЕРЕХОДА — БЕЗ ССЫЛКИ И БЕЗ СТРЕЛКИ (03.10.2026). Пустой `to` бывает у задания-напоминания
                и, в приложении, у задания на раздел, которого у ученика нет (`lib/taskLinks.ts`). Раньше строка
                всё равно была ссылкой со стрелкой: нажатие «никуда» выглядело как поломка. */}
            {task.to ? (
              <Link to={task.to} className="min-w-0 flex-1">
                <div className="truncate text-[14.5px] font-semibold text-on-surface">{task.title}</div>
                <div className="truncate text-[11.5px] text-on-surface-variant">{task.meta}</div>
              </Link>
            ) : (
              <div className="min-w-0 flex-1">
                <div className="truncate text-[14.5px] font-semibold text-on-surface">{task.title}</div>
                <div className="truncate text-[11.5px] text-on-surface-variant">{task.meta}</div>
              </div>
            )}
            {task.to && (
              <span className="material-symbols-outlined flex-none text-[17px] text-on-surface-variant">
                chevron_right
              </span>
            )}
          </div>
        ))}
        {personalToday.map((e, i) => (
          <Link
            key={e.id}
            to="/app/schedule"
            className={cn(
              'flex items-center gap-3 py-3',
              i < personalToday.length - 1 && 'border-b border-outline',
              isPast(e, today) && 'opacity-50',
            )}
          >
            {/* На месте галочки — время: запись не отмечается, а напоминает. */}
            <span className="w-9 flex-none text-center text-[11.5px] font-bold tabular-nums text-info">
              {e.start.toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' })}
            </span>
            <span className="min-w-0 flex-1 truncate text-[14.5px] font-semibold text-on-surface">{e.title}</span>
            <span className="material-symbols-outlined flex-none text-[17px] text-on-surface-variant">
              chevron_right
            </span>
          </Link>
        ))}
      </div>
      </>
      )}

      {/* ── Слово дня ────────────────────────────────────────────────────────
          ⚠️ На мобильной главной его НЕ БЫЛО ВОВСЕ (найдено 01.09.2026). Экран
          собирался по макету деки, и блок в него просто не попал — а в
          приложении человек видит именно эту главную, то есть слово дня не
          показывалось никому с телефона. При этом оно у нас в бесплатном
          наборе и названо в заметках проверяющему как то, что доступно сразу.

          Исчезает после изучения — как и на широком экране: повторять
          выученное слово незачем. */}
      <WordOfTheDayCard />

      {/* ── Упражнения: горизонтальная лента вместо сетки 3×2 ──────────────── */}
      <h2 className="px-0.5 pt-1 font-display text-[17px] font-bold">
        {t('dashboard.practiceTitle')}
      </h2>
      <div className="-mx-5 flex gap-2.5 overflow-x-auto px-5 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {tiles.map((tile, i) => (
          <Link
            key={tile.to}
            to={tile.to}
            className="w-[104px] flex-none rounded-[24px] bg-surface-container-low px-3 py-3.5"
          >
            <span
              className={cn(
                'mb-2 grid h-[34px] w-[34px] place-items-center rounded-full',
                i % 2 === 0 ? 'bg-primary-container text-primary' : 'bg-info-container text-info',
              )}
            >
              <span className="material-symbols-outlined text-[18px]">{tile.icon}</span>
            </span>
            <span className="block text-[12.5px] font-semibold leading-tight text-on-surface">
              {tile.label}
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}
